import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Phone, ShoppingBag, Plus, Info } from "lucide-react";
import { Auticko, AutickoLoader, PageHero } from "../../components";
import { GET_DAILY_MENU_URL, GET_PUBLIC_DELIVERY_SCHEDULES_URL } from "../../features/UrlHelper";
import { useTenant } from "../../context/TenantContext";
import { DAY_NAMES, DAY_SHORT_NAMES, ITEM_TYPE_LABELS, ITEM_TYPE_ORDER, ALLERGENS } from "../../features/orderConstants";
import "./DailyMenuPage.scss";

/** Keby bol zatvoreny cely tyzden -- pas dni by inak ostal prazdny bez vysvetlenia. */
const CELY_TYZDEN_ZATVORENY = "Tento týždeň prevádzka nerozváža.";

function getMonday(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function toISODateStr(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function formatShortDate(date) {
  return `${date.getDate()}. ${date.getMonth() + 1}.`;
}

// ─── Rozvrh – pomocné funkcie ─────────────────────────────────────────────────

function parseLocalDate(str) {
  if (!str) return null;
  if (typeof str === "string" && str.length > 10) {
    const d = new Date(str);
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }
  const [y, m, d] = str.split("-").map(Number);
  return new Date(y, m - 1, d);
}

function scheduleCoversDate(schedule, date) {
  const from = parseLocalDate(schedule.valid_from);
  const to = schedule.valid_to ? parseLocalDate(schedule.valid_to) : null;
  if (!from) return false;
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const f = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  if (d < f) return false;
  if (to) {
    const t = new Date(to.getFullYear(), to.getMonth(), to.getDate());
    if (d > t) return false;
  }
  return true;
}

// Vráti dnešný deň podľa ISO (1=Po, 7=Ne) v časovej zóne Bratislava
function getTodayInBratislava() {
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Bratislava",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    weekday: "short",
  });
  const parts = formatter.formatToParts(new Date());
  const year = Number(parts.find((p) => p.type === "year").value);
  const month = Number(parts.find((p) => p.type === "month").value);
  const day = Number(parts.find((p) => p.type === "day").value);
  const weekday = parts.find((p) => p.type === "weekday").value; // "Mon", "Tue" ...
  const SHORT_TO_ISO = { Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6, Sun: 7 };
  return { date: new Date(year, month - 1, day), dayOfWeek: SHORT_TO_ISO[weekday] ?? 1 };
}

// Vráti aktuálny čas v minútach od polnoci podľa Bratislavy
function getCurrentMinutesBratislava() {
  const formatter = new Intl.DateTimeFormat("sk-SK", {
    timeZone: "Europe/Bratislava",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const str = formatter.format(new Date()); // "08:30"
  const [h, m] = str.replace(/[^\d:]/g, "").split(":").map(Number);
  return h * 60 + m;
}

function formatTime(t) {
  if (!t) return "";
  return String(t).substring(0, 5); // "08:00:00" → "08:00"
}

function timeToMinutes(t) {
  if (!t) return null;
  const [h, m] = String(t).split(":").map(Number);
  return h * 60 + (m || 0);
}

/** ISO deň v týždni (1 = pondelok, 7 = nedeľa) pre `Date`. */
function isoDenVTyzdni(date) {
  const d = date.getDay(); // 0 = nedeľa
  return d === 0 ? 7 : d;
}

/**
 * Rozvrh platny pre dany datum, alebo null.
 *
 * Vynimka (`valid_to` vyplnene) ma prednost pred trvalym rozvrhom a medzi
 * rovnocennymi vyhrava neskorsie zacinajuci -- 1:1 s `rozvrhPreDatum`
 * v `backend/services/orderWindow.js`. Keby sa pravidla rozisli, stranka by
 * ukazovala menu na den, ktory server pri objednavke odmietne.
 */
function rozvrhPreDatum(schedules, date) {
  const platne = schedules.filter((s) => scheduleCoversDate(s, date));
  const vynimky = platne.filter((s) => s.valid_to !== null);
  const zdroj = vynimky.length > 0 ? vynimky : platne.filter((s) => s.valid_to === null);
  if (zdroj.length === 0) return null;

  const najnovsi = zdroj.reduce((a, b) =>
    (parseLocalDate(a.valid_from) >= parseLocalDate(b.valid_from) ? a : b));
  const den = isoDenVTyzdni(date);
  return najnovsi.days?.find((d) => d.day_of_week === den) ?? null;
}

/** Je prevadzka v tento den zatvorena? Chybajuci rozvrh = zatvorene (ako server). */
function jeZatvorene(schedules, date) {
  const rozvrh = rozvrhPreDatum(schedules, date);
  return !rozvrh || rozvrh.is_closed === true;
}

function getTodaySchedule(schedules) {
  const { date } = getTodayInBratislava();
  return rozvrhPreDatum(schedules, date);
}

/**
 * Stav dnesneho okna.
 *
 * S online objednavkami plati INE pravidlo nez pri telefone: zalezi len na
 * uzavierke (`time_to`), zaciatok sa neskuma -- kto si objedna obed o siedmej
 * rano, nerobi nic zle (R13). Je to to iste pravidlo, ake pocita server
 * v `services/orderWindow.js`; keby tu ostalo povodne, stranka by pred
 * otvorenim tvrdila "nerozvazame", hoci objednavka by presla.
 */
function getOrderStatus(todaySchedule, objednavanieOnline = false) {
  if (!todaySchedule || todaySchedule.is_closed) {
    return { type: "closed", label: "Dnes nerozvážame", hours: null };
  }
  const from = timeToMinutes(todaySchedule.time_from);
  const to = timeToMinutes(todaySchedule.time_to);
  const now = getCurrentMinutesBratislava();
  const hoursStr = `${formatTime(todaySchedule.time_from)} – ${formatTime(todaySchedule.time_to)}`;

  if (objednavanieOnline) {
    if (to !== null && now <= to) {
      return { type: "open", label: "Prijímame objednávky", hours: `objednávky do ${formatTime(todaySchedule.time_to)}` };
    }
    return { type: "outside", label: "Na dnes už neobjednáte", hours: hoursStr };
  }

  if (from !== null && to !== null && now >= from && now <= to) {
    return { type: "open", label: "Prijímame objednávky", hours: hoursStr };
  }
  return { type: "outside", label: "Momentálne nerozvážame", hours: hoursStr };
}

function getServingDateStr(servingDate) {
  if (!servingDate) return null;
  // Vždy extrahujem len "YYYY-MM-DD" časť bez parsingu ako UTC
  // Toto zabráni posunu o deň pri práci s UTC timestamps
  const str = String(servingDate);
  const match = str.match(/^\d{4}-\d{2}-\d{2}/);
  return match ? match[0] : null;
}

/**
 * Je jedlo prave teraz nedostupne? Obsluha ho vie odznacit natrvalo
 * (`unavailable`) alebo len do urciteho casu (`unavailable_until`) -- rovnaka
 * podmienka je na serveri v `orderConfigController`.
 */
function jeNedostupne(item) {
  if (!item.unavailable) return false;
  return !item.unavailable_until || new Date(item.unavailable_until) > new Date();
}

/**
 * Jedno jedlo v zozname -- jedlo dna aj jedlo zo stalej ponuky vyzeraju
 * rovnako.
 *
 * `odkazNaObjednavku` je null, ked prevadzka online objednavky nema alebo ked
 * je zobrazeny den uz za nami. Vtedy sa tlacidlo nekresli vobec.
 */
function PolozkaMenu({ item, odkazNaObjednavku }) {
  const nedostupne = jeNedostupne(item);

  return (
    <li className={`daily-menu__jedlo${nedostupne ? " daily-menu__jedlo--nedostupne" : ""}`}>
      <div className="daily-menu__jedlo-text">
        <span className="daily-menu__jedlo-nazov">
          {item.menu_code && <em>{item.menu_code}</em>}
          <span>{item.name}</span>
        </span>
        {item.description && (
          <span className="daily-menu__jedlo-popis">{item.description}</span>
        )}
        {item.allergens_list && (
          <span className="daily-menu__jedlo-alergeny">Alergény: {item.allergens_list}</span>
        )}
        {nedostupne && (
          <span className="daily-menu__jedlo-nedostupne">Momentálne nedostupné</span>
        )}
      </div>
      <div className="daily-menu__jedlo-cena">
        <strong>{Number(item.price).toFixed(2)} €</strong>
        {!nedostupne && odkazNaObjednavku && (
          <Link
            className="daily-menu__jedlo-objednat"
            to={`${odkazNaObjednavku}&polozka=${item.id}`}
          >
            <Plus size={15} aria-hidden="true" /> Objednať
          </Link>
        )}
      </div>
    </li>
  );
}

function DailyMenuPage() {
  // Prevadzku uz nacital TenantContext (GET /api/tenant/resolve) -- druhy
  // dopyt na /api/company by bol zbytocny.
  const { tenant: company } = useTenant();
  const [menuItems, setMenuItems] = useState([]);
  // Kým menu nepríde, ukazuje sa autíčko -- inak by na chvíľu svietilo
  // "Pre tento deň nie je dostupné žiadne menu", čo nie je pravda.
  const [nacitava, setNacitava] = useState(true);
  const [schedules, setSchedules] = useState([]);
  // Datum, nie index: zatvorene dni sa z pasu vyhadzuju, takze poradie sa
  // po nacitani rozvrhu posunie a index by ukazoval na uplne iny den.
  const [aktivnyDatum, setAktivnyDatum] = useState(null);

  const weekDates = useMemo(() => {
    const monday = getMonday(new Date());
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      return d;
    });
  }, []);

  useEffect(() => {
    axios
      .get(GET_PUBLIC_DELIVERY_SCHEDULES_URL)
      .then((res) => {
        if (Array.isArray(res.data)) setSchedules(res.data);
      })
      .catch(() => {});

    const dateFrom = toISODateStr(weekDates[0]);
    const dateTo = toISODateStr(weekDates[6]);
    axios
      .get(GET_DAILY_MENU_URL({ date_from: dateFrom, date_to: dateTo }))
      .then((res) => {
        console.log("GET_DAILY_MENU_URL Response:", res.data);
        const items = Array.isArray(res.data) ? res.data : (res.data?.items || res.data?.data || []);
        console.log("Parsed items:", items);
        items.forEach(item => {
          console.log(`Item: ${item.name}, serving_date: ${item.serving_date}, parsed: ${getServingDateStr(item.serving_date)}`);
        });
        setMenuItems(items);
      })
      .catch((err) => {
        console.error("Error fetching daily menu:", err);
      })
      .finally(() => setNacitava(false));
  }, [weekDates]);

  /*
   * Dni, ktore sa vobec ponuknu. Zatvoreny den (vikend, sviatok) v pase nie je
   * -- nema co ukazat a objednat sa nan aj tak neda; server ho odmietne
   * rovnakym pravidlom (`orderWindow.dniNaObjednanie`). Kym rozvrh nie je
   * nacitany (alebo sa ho nepodarilo nacitat), ukaze sa cely tyzden: lepsie
   * zbytocny den navyse nez prazdna stranka.
   */
  const otvoreneDni = useMemo(
    () => (schedules.length === 0 ? weekDates : weekDates.filter((d) => !jeZatvorene(schedules, d))),
    [schedules, weekDates]
  );

  /*
   * Predvoleny den: dnesok, inak najblizsi dalsi otvoreny, inak posledny
   * otvoreny v tyzdni. Bezi znova po nacitani rozvrhu -- dnesok moze byt
   * prave ten zatvoreny a vtedy by ostal vybraty den, ktory uz v pase nie je.
   */
  useEffect(() => {
    if (otvoreneDni.length === 0) { setAktivnyDatum(null); return; }
    const datumy = otvoreneDni.map(toISODateStr);
    setAktivnyDatum((aktualny) => {
      if (aktualny && datumy.includes(aktualny)) return aktualny;
      const dnes = toISODateStr(new Date());
      return datumy.find((d) => d >= dnes) ?? datumy[datumy.length - 1];
    });
  }, [otvoreneDni]);

  /*
   * Denne a stale menu su pre zakaznika jedno a to iste -- co sa dnes da
   * objednat. Zluci sa preto do jedneho zoznamu a vsetky kategorie su vidiet
   * naraz, bez prepinaca. V kategorii idu najprv jedla dna, potom stale.
   */
  const activeDateStr = aktivnyDatum;
  const dailyItems = menuItems.filter(
    (item) => getServingDateStr(item.serving_date) === activeDateStr
  );
  const permanentItems = menuItems.filter((item) => !item.serving_date);

  const groupedByType = [...dailyItems, ...permanentItems].reduce((acc, item) => {
    if (!acc[item.item_type]) acc[item.item_type] = [];
    acc[item.item_type].push(item);
    return acc;
  }, {});

  const sortedTypes = ITEM_TYPE_ORDER.filter((t) => groupedByType[t]);

  const todaySchedule = schedules.length > 0 ? getTodaySchedule(schedules) : undefined;
  const objednavanie = company?.onlineOrdersEnabled === true;
  const orderStatus =
    todaySchedule !== undefined ? getOrderStatus(todaySchedule, objednavanie) : null;

  // Odkaz na objednavaciu obrazovku s predvolenym dnom (R41). Den je len navrh
  // -- ci sa da objednat, rozhoduje server v `/api/order-config`, takze sa tu
  // okno neduplikuje. Na uz minuly den tlacidlo nedava zmysel, tam je null.
  const dnesStr = toISODateStr(new Date());
  const odkazNaObjednavku =
    objednavanie && activeDateStr && activeDateStr >= dnesStr
      ? `/objednavka?den=${activeDateStr}`
      : null;

  return (
    <div className="daily-menu">
      {/* To isté záhlavie ako objednávka, profil a registrácia. */}
      <PageHero
        eyebrow={company?.name || "Denné menu"}
        title="Denné"
        zvyraznene="menu"
        lead="Ponuka na celý týždeň. Vyberte si deň a pozrite sa, čo varíme."
        akcie={
          (objednavanie || company?.phone) && (
            <>
              {objednavanie && (
                <Link to="/objednavka" className="daily-menu__cta">
                  <ShoppingBag size={17} aria-hidden="true" /> Objednať online
                </Link>
              )}
              {company?.phone && (
                <a className="daily-menu__telefon" href={`tel:${company.phone}`}>
                  <Phone size={16} aria-hidden="true" />
                  {company.phone}
                </a>
              )}
            </>
          )
        }
        vedla={
          orderStatus && (
            <div className={`daily-menu__stav daily-menu__stav--${orderStatus.type}`}>
              <span className="daily-menu__stav-bodka" aria-hidden="true" />
              <div>
                <span className="daily-menu__stav-label">{orderStatus.label}</span>
                {orderStatus.hours && (
                  <span className="daily-menu__stav-hodiny">Dnes: {orderStatus.hours}</span>
                )}
              </div>
            </div>
          )
        }
      />

      <div className="daily-menu__obsah">
        {/* Pás dní ako na objednávke -- na mobile sa posúva prstom. */}
        <div className="daily-menu__dni" role="tablist" aria-label="Deň">
          {otvoreneDni.map((date) => {
            const datum = toISODateStr(date);
            // Nazov dna sa berie z datumu, nie z poradia v pase -- zatvorene
            // dni z neho vypadli, takze index uz dnu v tyzdni nezodpoveda.
            const den = isoDenVTyzdni(date) - 1;
            return (
              <button
                key={datum}
                type="button"
                role="tab"
                aria-selected={aktivnyDatum === datum}
                className={`daily-menu__den${aktivnyDatum === datum ? " daily-menu__den--aktivny" : ""}`}
                onClick={() => setAktivnyDatum(datum)}
              >
                <span className="daily-menu__den-nazov">{DAY_NAMES[den]}</span>
                <span className="daily-menu__den-skratka">{DAY_SHORT_NAMES[den]}</span>
                <span className="daily-menu__den-datum">{formatShortDate(date)}</span>
              </button>
            );
          })}
        </div>

        {nacitava ? (
          <AutickoLoader text="Načítavam menu…" />
        ) : otvoreneDni.length === 0 ? (
          <p className="daily-menu__prazdne">
            <Auticko sirka={88} />
            {CELY_TYZDEN_ZATVORENY}
          </p>
        ) : (
          <>
            {sortedTypes.length === 0 && (
              <p className="daily-menu__prazdne">
                <Auticko sirka={88} />
                Pre tento deň nie je dostupné žiadne menu.
              </p>
            )}

            {sortedTypes.length > 0 && (
              <section className="daily-menu__sekcia">
                <header className="daily-menu__sekcia-hlavicka">
                  <h2 className="daily-menu__sekcia-nadpis">Denné menu</h2>
                </header>

                {sortedTypes.map((type) => (
                  <div key={type} className="daily-menu__skupina">
                    <h3 className="daily-menu__skupina-nadpis">{ITEM_TYPE_LABELS[type] ?? type}</h3>
                    {type === "SOUP" && (
                      <p className="daily-menu__poznamka">
                        <Info size={16} aria-hidden="true" />
                        Polievka je zahrnutá v cene denného menu. V prípade samostatnej
                        objednávky (bez hlavného jedla) platí cena uvedená pri polievke.
                      </p>
                    )}
                    <ul className="daily-menu__jedla">
                      {groupedByType[type].map((item) => (
                        <PolozkaMenu key={item.id} item={item} odkazNaObjednavku={odkazNaObjednavku} />
                      ))}
                    </ul>
                  </div>
                ))}
              </section>
            )}

            {sortedTypes.length > 0 && (
              <section className="daily-menu__sekcia daily-menu__alergeny">
                <header className="daily-menu__sekcia-hlavicka">
                  <h2 className="daily-menu__sekcia-nadpis">Zoznam alergénov</h2>
                </header>
                <ul className="daily-menu__alergeny-zoznam">
                  {ALLERGENS.map((allergen) => (
                    <li key={allergen.id}>
                      <span className="daily-menu__alergen-cislo">{allergen.id}</span>
                      {allergen.label}
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default DailyMenuPage;
